import PropTypes from 'prop-types';
import React from 'react';
import NoSSR from 'react-no-ssr';
import shortid from 'shortid';
import FgbFeaturedEmployeeSlide from '../../../components/CompanyPages/Sections/FgbFeaturedEmployeeSlide/FgbFeaturedEmployeeSlide';
import VideoPlayer from '../../../components/CompanyPages/Sections/VideoPlayer/VideoPlayer';
import EditorialContentSection from '../../../components/EditorialContent/EditorialContentSection/EditorialContentSection';
import LoadingSpinner from '../../../components/Elements/LoadingSpinner/LoadingSpinner';
import {
    getEventStatus,
    startingSoon,
} from '../../../components/Events/helperFunctions';
import InPageEventDetailSection from '../../../components/Events/InPageEventDetailSection/InPageEventDetailSection';
import InProgressBanner from '../../../components/Events/InProgressBanner/InProgressBanner';
import ParticipatingEmployersGrid from '../../../components/Events/ParticipatingEmployersGrid/ParticipatingEmployersGrid';
import SingleEventPageBanner from '../../../components/Events/SingleEventPageBanner/SingleEventPageBanner';
import SingleEventPageBottomCard from '../../../components/Events/SingleEventPageBottomCard/SingleEventPageBottomCard';
import WithRegisterStateManager from '../../../components/Events/WithRegisterStateManager/WithRegisterStateManager';
import { eventStatusTypes } from '../../../components/Includes/Constants/events';
import { eventPropTypesShape } from '../../../components/Includes/eventsProps';
import { buildImagePath } from '../../../components/JobComponents/Company/Image/logo';
import Slider from '../../../components/JobComponents/Silder/Slider';
import CommonCardShell from '../../../components/Layout/CommonCardShell/CommonCardShell';
import Layout from '../../../components/Layout/Layout';
import { Consumer as PageConsumer } from '../../../contexts/PageContext';
import './SingleEvent.scss';

export const formatSponsorNames = (sponsors) => {
    if (!sponsors || sponsors.length === 0) return 'Fairygodboss';
    if (sponsors.length === 1) return sponsors[0].name;
    if (sponsors.length === 2)
        return `${sponsors[0].name} and ${sponsors[1].name}`;
    if (sponsors.length === 3)
        return `${sponsors[0].name}, ${sponsors[1].name}, and ${sponsors[2].name}`;
    return 'Fairygodboss';
};

const sponsorImageTransforms = {
    companyLogo: 'ar_1:1,c_lpad/c_scale,f_auto,q_auto,dpr_auto,fl_lossy,w_280',
};

const speakerSliderSettings = {
    dots: true,
    arrows: true,
    infinite: true,
    autoplay: false,
    autoplaySpeed: 1000,
    speed: 1000,
    slidesToShow: 1,
    slidesToScroll: 1,
    centerMode: false,
    adaptiveHeight: true,
    swipe: true,
    variableWidth: false,
};

export class SingleEvent extends React.PureComponent {
    constructor(props) {
        super(props);
        const startingSoonDecision = startingSoon(props.event.startTime);
        const eventStatus = getEventStatus(
            props.event.startTime,
            props.event.endTime
        );
        this.state = {
            eventStatus: startingSoonDecision
                ? eventStatusTypes.STARTING_SOON
                : eventStatus,
        };
    }

    static propTypes = {
        /**
         * single event data
         */
        event: eventPropTypesShape.isRequired,
        attendeeCount: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.number,
        ]),
        attendeeImages: PropTypes.arrayOf(
            PropTypes.shape({
                src: PropTypes.string,
                alt: PropTypes.string,
            })
        ),
        /**
         * whether user is register for the event or not
         */
        isUserRegistered: PropTypes.bool.isRequired,
        /**
         * set userRegistered to true
         */
        setUserAsRegistered: PropTypes.func.isRequired,
        /**
         * page has bottom banner?
         */
        hasBottomBanner: PropTypes.bool,
    };

    static defaultProps = {
        event: {},
        attendeeImages: [],
        attendeeCount: null,
        hasBottomBanner: false,
    };

    renderEventDetailsSection = (eventRegistrationProps) => {
        const {
            event: { description },
        } = this.props;

        return (
            <section className="event-details-section">
                <div className="single-event-page-title-display">
                    Event Details
                </div>
                <div className="content-controlled-width">
                    {description && (
                        <EditorialContentSection content={description} />
                    )}
                    <InPageEventDetailSection {...eventRegistrationProps} />
                </div>
            </section>
        );
    };

    renderSponsorSection = () => {
        const {
            event: { sponsors },
        } = this.props;
        if (!sponsors || sponsors.length === 0) return null;

        // if less than 4 sponsors, show legacy sponsor detail section
        if (sponsors.length <= 3) {
            return this.renderDetailedSponsorSection(sponsors);
        }

        // if 4+ sponsors, show participating employer logo grid
        const attendingEmployers = sponsors.map((sponsor) => ({
            ...sponsor,
            key: shortid(),
        }));
        return (
            <ParticipatingEmployersGrid
                attendingEmployers={attendingEmployers}
            />
        );
    };

    renderDetailedSponsorSection = (sponsors) => {
        return (
            <section className="single-event-page-sponsors-section">
                <div className={'single-event-page-title-display sponsors'}>
                    Presented by {formatSponsorNames(sponsors)}
                </div>
                {sponsors.map((sponsor, idx) => {
                    const marginStyling = idx === 0 ? '' : 'margin-top-styling';

                    const hasSponsorImage = sponsor.image && sponsor.image.src;

                    const imageSection = hasSponsorImage ? (
                        <div className="large-sponsor-logo-container">
                            <img
                                className="large-sponsor-logo"
                                alt={sponsor.image.alt || ''}
                                src={buildImagePath(
                                    sponsor.image.src,
                                    'companyLogo',
                                    sponsorImageTransforms
                                )}
                            />
                        </div>
                    ) : null;

                    return (
                        <div
                            className={`single-sponsor-section ${marginStyling}`}
                            key={shortid()}
                        >
                            {imageSection}
                            <div className="sponsor-description content-controlled-width">
                                {sponsor.description}
                            </div>
                        </div>
                    );
                })}
            </section>
        );
    };

    renderOptionalSpeakerSection = () => {
        const {
            event: { speakers },
        } = this.props;

        if (!speakers || speakers.length === 0) return null;

        let speakerInfo = {
            name: speakers[0].name,
            image: speakers[0].image,
            location: speakers[0].location,
            role: speakers[0].shortBio,
            bio: speakers[0].bio,
        };

        return (
            <NoSSR onSSR={<LoadingSpinner />}>
                <section className="speaker-section">
                    <div className={'single-event-page-title-display speakers'}>
                        Who You'll Hear From
                    </div>
                    {speakers.length === 1 ? (
                        <FgbFeaturedEmployeeSlide {...speakerInfo} />
                    ) : (
                        <Slider settings={speakerSliderSettings}>
                            {speakers.map((speaker) => {
                                speakerInfo = {
                                    name: speaker.name,
                                    image: speaker.image,
                                    location: speaker.location,
                                    role: speaker.shortBio,
                                    bio: speaker.bio,
                                };
                                const itemKey = shortid();
                                return (
                                    <div key={itemKey}>
                                        <FgbFeaturedEmployeeSlide
                                            {...speakerInfo}
                                        />
                                    </div>
                                );
                            })}
                        </Slider>
                    )}
                </section>
            </NoSSR>
        );
    };

    renderGoogleSeo = () => {
        return (
            <div
                className="google-seo"
                dangerouslySetInnerHTML={this.convertGoogleSeoString()}
            />
        );
    };

    convertGoogleSeoString = () => {
        const {
            event: { googleSeo },
        } = this.props;
        const content = `<script type='application/ld+json'>${googleSeo}</script>`;
        return { __html: content };
    };

    render() {
        const {
            event,
            event: { video },
            isUserRegistered,
            setUserAsRegistered,
            attendeeCount,
            attendeeImages,
            hasBottomBanner,
        } = this.props;

        const { eventStatus } = this.state;

        const eventRegistrationProps = {
            event,
            isUserRegistered,
            setUserAsRegistered,
        };

        const headerBannerProps = {
            ...eventRegistrationProps,
            attendeeCount,
            attendeeImages,
            eventStatus,
        };

        const videoType =
            video &&
            video.src &&
            (video.src.indexOf('youtube') >= 0 ||
            video.src.indexOf('youtu.be') >= 0
                ? 'youtube'
                : 'cloudinary');

        return (
            <div className="page-Events-SingleEvent">
                <PageConsumer>
                    {(context) => {
                        return (
                            <Layout
                                PAGE_PROPS={context.getPageProps()}
                                hasBottomBanner={hasBottomBanner}
                                hasChangingContainer={false}
                                fullScreenStyling
                            >
                                {(eventStatus ===
                                    eventStatusTypes.STARTING_SOON ||
                                    eventStatus ===
                                        eventStatusTypes.IN_PROGRESS) &&
                                    event.userRegistered && (
                                        <InProgressBanner
                                            eventStatus={eventStatus}
                                            joinUrl={event.joinUrl}
                                        />
                                    )}
                                <SingleEventPageBanner
                                    event
                                    {...headerBannerProps}
                                />
                                <section className="card-controlled-width">
                                    {video &&
                                        video.src &&
                                        videoType &&
                                        eventStatus ===
                                            eventStatusTypes.EVENT_OVER && (
                                            <CommonCardShell
                                                className={'video-card'}
                                            >
                                                <div className="large-content-controlled-width video-controlled-width">
                                                    <div className="video-text">
                                                        This event is over, and
                                                        you can view the
                                                        recording here:
                                                    </div>
                                                    <VideoPlayer
                                                        videoSrc={video.src}
                                                        videoType={videoType}
                                                        fullWidth={
                                                            videoType ===
                                                            'cloudinary'
                                                        }
                                                    />
                                                </div>
                                            </CommonCardShell>
                                        )}
                                    <CommonCardShell>
                                        <div className="large-content-controlled-width">
                                            {this.renderEventDetailsSection(
                                                eventRegistrationProps
                                            )}
                                            {this.renderSponsorSection()}
                                            {this.renderOptionalSpeakerSection()}
                                        </div>
                                    </CommonCardShell>
                                    {eventStatus ===
                                        eventStatusTypes.NOT_STARTED && (
                                        <CommonCardShell className="bottom-card">
                                            <div className="content-controlled-width">
                                                <SingleEventPageBottomCard
                                                    {...eventRegistrationProps}
                                                />
                                            </div>
                                        </CommonCardShell>
                                    )}
                                </section>
                            </Layout>
                        );
                    }}
                </PageConsumer>
                {this.renderGoogleSeo()}
            </div>
        );
    }
}

export default WithRegisterStateManager(SingleEvent);
